<!--
  File name: CrudTableExpand.vue
  Program Description:
    - This is similar to the CrudTable component but with added functionalities
    including the showcase of additional information and setting deadlines for
    ASIR
    - It is used for ASIR and PDBNF pages

-->

<template>
  <v-container flex>
    <template>
      <v-row class="flex-grow-0 flex-shrink-0">
        <v-col>
          <v-card
            tile
            elevation="0"
          >
            <v-card-title :style="'background-color: #d0d3d9; padding: 0'">
              <div
                class="text-h4"
              >
                <b>{{ tableHeader }}</b>
              </div>
              <v-spacer />
              <template v-for="(item) in opts.tableActions">
                <template v-if="item.id === 'create'">
                  <v-btn
                    v-if="showLabel(item.privilege || `canCreate${roleName}`)"
                    :id="`${item.id}-btn-default`"
                    :key="`${item.id}-btn-default`"
                    v-bind="item.vBind"
                    text
                    :color="item.color ? item.color : 'green'"
                    :loading="loading"
                    @click="item.action ? item.action() : $router.push({ path: `${parentId}${collectionName}/create`})"
                  >
                    <v-icon class="mr-2">
                      {{ item.icon || 'mdi-plus-circle-outline' }}
                    </v-icon> {{ getLabel(item.privilege || `canCreate${roleName}`, item.label) }}
                  </v-btn>
                </template>
                <template v-else-if="item.id === 'deadline' && isAdmin && collectionName === 'SecurityIncident'">
                  <v-btn
                    v-if="showLabel(item.privilege)"
                    :id="`${item.id}-btn-default`"
                    :key="`${item.id}-btn-default`"
                    v-bind="item.vBind"
                    text
                    :color="item.color"
                    :loading="loading"
                    @click="item.action()"
                  >
                    <v-icon class="mr-2">
                      {{ item.icon || 'mdi-plus-circle-outline' }}
                    </v-icon> {{ getLabel(item.privilege, item.label) }}
                  </v-btn>
                  <v-dialog
                    :key="`${item.id}-dialog`"
                    v-model="showDeadlineDialog"
                    persistent
                    max-width="575px"
                  >
                    <v-card
                      class="elevation-12 px-10 py-6 rounded-lg"
                      tile
                    >
                      <v-btn
                        style="position:absolute; top:5px; right:5px;"
                        text
                        @click="close()"
                      >
                        <v-icon>
                          mdi-close
                        </v-icon>
                      </v-btn>
                      <v-card-title
                        class="title pa-2"
                      >
                        <h3 class="primary--text font-weight-bold">
                          ASIR Deadline
                        </h3>
                        <v-spacer />
                        <v-btn
                          v-if="showLabel(item.privilege) && !editDeadline"
                          :id="`edit-${item.id}-btn`"
                          class="white--text text-body-2 px-3 ma-3"
                          color="#d6b315"
                          style="text-transform: none"
                          :loading="loading"
                          elevation="0"
                          @click="editDeadline = true"
                        >
                          <v-icon class="text-h6 mr-2">
                            {{ 'mdi-pencil' }}
                          </v-icon>
                          {{ getLabel(item.privilege, 'Edit') }}
                        </v-btn>
                      </v-card-title>
                      <v-card-text>
                        <v-row
                          v-if="!editDeadline"
                          class="py-10"
                        >
                          <v-col
                            class="pa-0 d-flex flex-column"
                            cols="6"
                          >
                            <span class="text-subtitle-2">
                              Start Month
                            </span>
                            <h3 class="black--text text-h6">
                              {{ defaultStartMonth }}
                            </h3>
                          </v-col>
                          <v-col
                            class="pa-0 d-flex flex-column"
                            cols="6"
                          >
                            <span class="text-subtitle-2">
                              End Month
                            </span>
                            <h3 class="black--text text-h6">
                              {{ defaultEndMonth }}
                            </h3>
                          </v-col>
                        </v-row>
                        <template v-if="editDeadline">
                          <v-row
                            class="py-10 justify-space-around"
                          >
                            <v-col
                              class="pa-0"
                              cols="5"
                              style="position: relative"
                            >
                              <VAutocomplete
                                v-model="startMonth"
                                label="Start Month"
                                dense
                                :items="$store.state.defaults.month"
                                outlined
                                :disabled="loading"
                                :append-icon="''"
                                :error-messages="genericError('startMonth','Start Month')"
                                @input="$v.startMonth.$touch()"
                                @blur="$v.startMonth.$touch()"
                              />
                              <v-icon
                                disabled
                                class="calendarIcon"
                              >
                                mdi-calendar
                              </v-icon>
                            </v-col>
                            <v-col
                              class="pa-0"
                              cols="5"
                              style="position: relative"
                            >
                              <VAutocomplete
                                v-model="endMonth"
                                label="End Month"
                                dense
                                :items="$store.state.defaults.month"
                                outlined
                                :disabled="loading"
                                :append-icon="''"
                                :error-messages="genericError('endMonth','End Month')"
                                @input="$v.endMonth.$touch()"
                                @blur="$v.endMonth.$touch()"
                              />
                              <v-icon
                                disabled
                                class="calendarIcon"
                              >
                                mdi-calendar
                              </v-icon>
                            </v-col>
                          </v-row>
                          <v-card-actions class="justify-end py-0">
                            <v-btn
                              color="primary"
                              text
                              @click="openDeadline"
                            >
                              Cancel
                            </v-btn>
                            <v-btn
                              class="px-12"
                              color="primary"
                              @click="setDeadline"
                            >
                              Set
                            </v-btn>
                          </v-card-actions>
                        </template>
                      </v-card-text>
                    </v-card>
                  </v-dialog>
                </template>
                <template
                  v-else-if="item.id === 'delete'"
                >
                  <v-btn
                    v-if="showLabel(item.privilege || `canDelete${roleName}`)"
                    :id="`${item.id}-btn-default`"
                    :key="`${item.id}-btn-default`"
                    text
                    color="red"
                    :loading="loading"
                    v-bind="item.vBind"
                    @click="item.action ? item.action() : deleteDialog(item.callback)"
                  >
                    <v-icon class="mr-2">
                      {{ item.icon || 'mdi-delete' }}
                    </v-icon> {{ getLabel(item.privilege || `canDelete${roleName}`, item.label) }}
                  </v-btn>
                </template>
                <template v-else>
                  <v-btn
                    v-if="showLabel(item.privilege)"
                    :id="`${item.id}Btn`"
                    :key="`${item.id}-btn-default`"
                    text
                    :color="item.color"
                    :loading="loading"
                    @click="item.action"
                  >
                    <v-icon class="mr-2">
                      {{ item.icon || '' }}
                    </v-icon> {{ item.privilege ? getLabel(item.privilege, item.label) : item.label }}
                  </v-btn>
                </template>
              </template>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>
      <!-- <v-row>
        <v-col class="pb-0">
          <v-card tile>
            <v-card-text class="d-flex flex-row-reverse pt-2 pb-2">
              <v-btn
                v-if="showLabel(`canDelete${roleName}`)"
                :id="`delete-btn-default`"
                :key="`delete-btn-default`"
                small
                outlined
                color="red"
                :loading="loading"
                :disabled="selectedLines.length === 0"
                @click="deleteDialog()"
              >
                <v-icon class="mr-2">
                  {{ 'mdi-delete' }}
                </v-icon> {{ getLabel(`canDelete${roleName}`, 'Delete') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row> -->
      <v-row class="flex-grow-1">
        <v-col class="pt-0">
          <v-tabs
            v-if="!isPic && !onlyAdmin && collectionName === 'DataBreach'"
            v-model="selectedTab"
            grow
          >
            <v-tab
              v-for="( tab, key ) in tabs"
              :id="`${tab}-tab-button`"
              :key="key"
              :disabled="loading"
            >
              {{ tab }}
            </v-tab>
          </v-tabs>
          <v-tabs-items
            v-model="selectedTab"
            class="shadow radius rounded-0"
          >
            <v-tab-item
              v-for="( tab, key ) in tabs"
              :key="key"
              :transition="false"
            />
            <v-card tile>
              <v-card-text>
                <v-data-table
                  v-model="selectedLines"
                  :show-select="!hideSelect"
                  :headers="headers"
                  :items="opts.listFilter ? listFilter(list) : list"
                  :options.sync="options"
                  :server-items-length="total"
                  :footer-props="footerProps"
                  :loading="loading"
                  :single-expand="opts.singleExpand"
                  :expanded.sync="expanded"
                  :show-expand="opts.showExpand"
                  item-key="_id"
                  v-bind="vBind"
                  @update:options="populateTable(enteredSearchWord ? 'search' : '')"
                  @toggle-select-all="disableSelect && selectAllToggle"
                  @click:row="rowClick"
                >
                  <template
                    v-if="collectionName === 'DataBreach'"
                    v-slot:[`item.organization`]="{ item }"
                  >
                    {{ item.pic.organization }}
                  </template>
                  <template
                    v-if="collectionName === 'DataBreach'"
                    v-slot:[`item.withRequest`]="{ item }"
                  >
                    {{ item.withRequest === 'No' ? 'None' : 'Yes' }}
                  </template>
                  <template
                    v-if="collectionName === 'DataBreach'"
                    v-slot:[`item.recipient`]="{ item }"
                  >
                    <p
                      v-if="item.recipient.adjudication.fullName"
                      class="ma-0"
                    >
                      {{ item.recipient.adjudication.fullName }}
                    </p>
                    <p
                      v-if="item.recipient.investigation.fullName"
                      class="ma-0"
                    >
                      {{ item.recipient.investigation.fullName }}
                    </p>
                    <p
                      v-if="item.recipient.enforcement.fullName"
                      class="ma-0"
                    >
                      {{ item.recipient.enforcement.fullName }}
                    </p>
                  </template>
                  <template
                    v-if="collectionName === 'DataBreach'"
                    v-slot:[`item.dateOfOccurrence`]="{ item }"
                  >
                    {{ removeTime(item.dateOfOccurrence) }}
                  </template>
                  <template
                    v-if="collectionName === 'DataBreach'"
                    v-slot:[`item.dateOfNotification`]="{ item }"
                  >
                    {{ item.status.value !== 'Drafted' ? item.dateOfNotification : '' }}
                  </template>
                  <template
                    v-if="collectionName === 'DataBreach'"
                    v-slot:[`item.reviewer`]="{ item }"
                  >
                    {{ item.assignedTo.firstPass.fullName }}<br>{{ item.assignedTo.final.fullName }}
                  </template>
                  <template
                    v-if="collectionName === 'DataBreach'"
                    v-slot:[`item.status`]="{ item }"
                  >
                    <StatusChip
                      :status="item.status.value"
                      :is-active="item._status === 'active'"
                      :label="item.formattedStatus"
                    />
                  </template>
                  <template
                    v-else
                    v-slot:[`item.status`]="{ item }"
                  >
                    <StatusChip :status="item.status" />
                  </template>
                  <template
                    v-slot:[`item.data-table-expand`]="{ isExpanded, expand }"
                  >
                    <v-icon
                      @click="expand(!isExpanded)"
                    >
                      {{ isExpanded ? 'mdi-minus' : 'mdi-plus' }}
                    </v-icon>
                  </template>
                  <template
                    v-if="opts.showExpand"
                    v-slot:expanded-item="{ item }"
                  >
                    <td
                      class="pa-5 pl-16"
                      :colspan="headers.length + 1"
                    >
                      <table
                        class="text-left"
                        style="width:100%; border-spacing: 5px;"
                      >
                        <tr>
                          <th>
                            Status History
                          </th>
                        </tr>
                        <tr class="ma-2">
                          <th
                            v-for="( header, key ) in opts.expandHeaders"
                            :key="key"
                            style="width:33.3%"
                          >
                            {{ isPic && (header === 'Modified By') ? '' : header }}
                          </th>
                        </tr>
                        <tr
                          v-for="(history, key) in item.statusHistory"
                          :key="key"
                          class="ma-2"
                        >
                          <td class="py-2">
                            {{ history.status }}
                          </td>
                          <td class="py-2">
                            {{ toDateAndTime(history.date) }}
                          </td>
                          <td
                            v-if="!isPic"
                            class="py-2"
                          >
                            {{ history.modifiedBy }}
                          </td>
                        </tr>
                        <template v-if="!isPic">
                          <tr>
                            <th
                              v-if="collectionName === 'DataBreach'"
                              class="pt-3"
                            >
                              Assigned Users
                            </th>
                          </tr>
                          <tr v-if="collectionName === 'DataBreach'">
                            <th
                              v-for="( header, key) in opts.assignedUserHeaders"
                              :key="key"
                            >
                              {{ header }}
                            </th>
                          </tr>
                          <tr v-if="collectionName === 'DataBreach'">
                            <td
                              v-if="item.assignedTo.evaluator.fullName"
                              class="py-2"
                            >
                              {{ item.assignedTo.evaluator.fullName }}
                            </td>
                            <td
                              v-else
                              class="py-2"
                            >
                              No data to display.
                            </td>
                            <td
                              v-if="item.assignedTo.firstPass.fullName"
                              class="py-2"
                            >
                              {{ item.assignedTo.firstPass.fullName }}
                            </td>
                            <td
                              v-else
                              class="py-2"
                            >
                              No data to display.
                            </td>
                            <td
                              v-if="item.assignedTo.final.fullName"
                              class="py-2"
                            >
                              {{ item.assignedTo.final.fullName }}
                            </td>
                            <td
                              v-else
                              class="py-2"
                            >
                              No data to display.
                            </td>
                          </tr>
                        </template>
                      </table>
                    </td>
                  </template>
                  <template
                    v-if="disableSelect"
                    v-slot:[`item.data-table-select`]="{ item, isSelected, select }"
                  >
                    <v-simple-checkbox
                      :ripple="false"
                      :value="isSelected"
                      :readonly="!item.isSelectable"
                      :disabled="!item.isSelectable"
                      @input="select($event)"
                    />
                  </template>

                  <template v-slot:top>
                    <v-row>
                      <v-col>
                        <v-menu
                          v-model="filter.menu"
                          :close-on-content-click="false"
                          :nudge-width="200"
                          max-width="300"
                          min-width="300"
                          offset-y
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              id="filter-btn"
                              text
                              v-bind="attrs"
                              v-on="on"
                              @click="getAllItems"
                            >
                              <v-icon large>
                                mdi-filter-variant
                              </v-icon>
                            </v-btn>
                          </template>
                          <v-card
                            class="pa-3"
                            :loading="filter.loading"
                          >
                            <v-card-title
                              class="px-0 text-h6 font-weight-medium"
                              style="color:#162b54;"
                            >
                              Filters
                              <v-spacer />
                              <v-btn
                                icon
                                @click="resetFilter(), populateTable()"
                              >
                                <v-icon>
                                  mdi-refresh
                                </v-icon>
                              </v-btn>
                            </v-card-title>
                            <v-combobox
                              v-model="filter.header"
                              outlined
                              dense
                              label="Filter"
                              :disabled="filter.loading || loading"
                              :loading="filter.loading"
                              :items="headerVal"
                            />
                            <v-combobox
                              v-model="filter.boxVal"
                              :items="filter.checkbox"
                              :label="`Search ${filter.header}`"
                              :disabled="filter.checkbox.length === 0 || loading"
                              :dense="filter.boxVal.length === 0"
                              :loading="filter.loading"
                              :menu-props="closeMenu"
                              outlined
                              chips
                              multiple
                              deletable-chips
                            />
                          </v-card>
                        </v-menu>
                      </v-col>
                      <v-col>
                        <v-text-field
                          v-if="showLabel(opts.privilege || `canViewAll${roleName}`)"
                          ref="search"
                          v-model.trim="searchWord"
                          :prepend-icon="appendSearchIcon ? 'search' : ''"
                          :append-icon="appendSearchIcon? '' : 'search'"
                          :label="getLabel(opts.privilege || `canViewAll${roleName}`, 'Search')"
                          single-line
                          outlined
                          dense
                          hide-details
                          :disabled="loading"
                          @keyup.enter="enteredSearchWord = true, populateTable('search'), options.page = 1"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="pt-0">
                        <v-divider />
                      </v-col>
                    </v-row>
                  </template>
                  <template
                    v-if="multipleUpdate"
                    v-slot:[`item.active`]="{item}"
                  >
                    <v-spacer />
                    <v-switch
                      v-model="item.active"
                      :disabled="!updateStatus"
                    />
                  </template>
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-spacer />
                    <template v-for="action in opts.entryActions">
                      <template v-if="action.id === 'view' || action.id === 'edit'">
                        <v-btn
                          v-if="showLabel(action.privilege || `can${capitalize(action.id)}${roleName}`)"
                          :id="`${action.id}TableBtn`"
                          :key="action.id"
                          icon
                          :color="getIconColor({
                            role: (action.privilege || `can${capitalize(action.id)}${roleName}`),
                            successColor: action.color
                          })"
                          @click="action.action ? action.action(item) : $router.push({ path: `${parentId}${collectionName}/${action.id}/${item._id}` })"
                        >
                          <v-tooltip left>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                              >
                                {{ action.icon }}
                              </v-icon>
                            </template>
                            <span>{{ capitalize(action.id) }} {{ collectionLabel }}</span>
                          </v-tooltip>
                        </v-btn>
                      </template>
                      <template v-if="action.id === 'delete'">
                        <v-btn
                          v-if="userRoleIs('admin')"
                          id="deleteTableBtn"
                          :key="action.id"
                          color="primary"
                          icon
                          @click="confirm(item)"
                        >
                          <v-tooltip left>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                              >
                                {{ action.icon }}
                              </v-icon>
                            </template>
                            <span>{{ capitalize(action.id) }} {{ collectionLabel }}</span>
                          </v-tooltip>
                        </v-btn>
                      </template>
                      <template v-if="action.id === 'assign' && canAssign">
                        <v-btn
                          v-if="showLabel(action.privilege || `can${capitalize(action.id)}${roleName}`)"
                          :id="`${action.id}TableBtn`"
                          :key="action.id"
                          icon
                          :color="getIconColor({
                            role: (action.privilege || `can${capitalize(action.id)}${roleName}`),
                            successColor: action.color
                          })"
                          :disabled="priority !== item._id"
                          @click="formItem = { ...item }, showAssignDialog = true"
                        >
                          <v-tooltip left>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                              >
                                {{ action.icon }}
                              </v-icon>
                            </template>
                            <span>{{ capitalize(action.id) }} {{ collectionLabel }}</span>
                          </v-tooltip>
                        </v-btn>
                      </template>
                      <template v-if="action.id === 'delete-pdbnf' && isAdmin">
                        <v-btn
                          v-if="showLabel(action.privilege || `can${capitalize(action.id)}${roleName}`)"
                          :id="`${action.id}TableBtn`"
                          :key="action.id"
                          :disabled="!canDeactivatePDBNF({ item, action})"
                          icon
                          :color="getIconColor({
                            role: (action.privilege || `can${capitalize(action.id)}${roleName}`),
                            successColor: action.color
                          })"
                          @click="deactivatePDBNF(item)"
                        >
                          <v-tooltip left>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                :disabled="!canDeactivatePDBNF({ item, action})"
                                color="green"
                                small
                                v-bind="attrs"
                                v-on="on"
                              >
                                {{ action.icon }}
                              </v-icon>
                            </template>
                            <span>Invalidate {{ item.dbnNumber }}</span>
                          </v-tooltip>
                        </v-btn>
                      </template>
                    </template>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tabs-items>
        </v-col>
      </v-row>
    </template>
    <AssignUserModal
      v-if="inAssignTab"
      v-model="showAssignDialog"
      queuing
      :form-item="formItem"
      @assignUser="priority = '', populateTable()"
    />
  </v-container>
</template>

<script>
import crud from '../utils/rest/crud'
import { VTextField, VRow, VCol } from 'vuetify/lib'
import { privilegeMixin } from 'session-plugin'
import { pluralize, dateAndTimeCreated } from '../utils/helpers'
import { validationMixin, custom, validationErrors } from '@/utils/validations'
import AssignUserModal from '@/components/AssignUserModal'
import deadline from '@/utils/webapi/deadline'
import StatusChip from '@/components/StatusChip'

export default {
  name: 'CrudTable',
  components: {
    VTextField,
    VRow,
    VCol,
    AssignUserModal,
    StatusChip
  },
  mixins: [
    privilegeMixin,
    validationMixin
  ],
  props: {
    name: {
      type: String,
      default: ''
    },
    meta: {
      type: Function,
      default: () => 1
    },
    vuex: {
      type: Object,
      default: () => {}
    },
    itemSelected: {
      type: String,
      default: 'XXX'
    },
    mdTables: {
      type: Object,
      default: undefined
    }
  },
  data: () => ({
    expanded: [],
    disabledCount: 0,
    selectedLines: [],
    searchWord: '',
    enteredSearchWord: false,
    total: 0,
    list: [],
    loading: false,
    showDeadlineDialog: false,
    showAssignDialog: false,
    editDeadline: false,
    deadlineId: '',
    startMonth: '',
    endMonth: '',
    defaultStartMonth: '',
    defaultEndMonth: '',
    options: {
      page: 1,
      itemsPerPage: 10
    },
    footerProps: {
      'items-per-page-options': [5, 10, 25, 50]
    },
    filter: {
      menu: false,
      header: '',
      checkbox: [],
      boxVal: [],
      loading: false,
      init: false
    },
    allItems: [],
    formItem: {},
    file: null,
    importDialog: false,
    search: null,
    searchKey: null,
    evaluator: '',
    reviewer: '',
    finalReviewer: '',
    priority: '',
    selectedTab: 0,
    assignTab: false,
    selectedPdbnf: {},
    init: false,
    query: ''
  }),
  validations () {
    return {
      ...custom.assignStatus,
      ...custom.deadline
    }
  },
  computed: {
    ...validationErrors,
    closeMenu () {
      return this.loading ? { value: false } : {}
    },
    module () {
      return this.$props.mdTables[this.name](this)
    },
    form () {
      return this.module.form
    },
    schema () {
      return this.form.schema.deadline
    },
    collectionName () {
      return this.opts.collectionName
    },
    collection () {
      return crud('deadline')
    },
    roleName () {
      return this.capitalize(this.collectionName)
    },
    opts () {
      return this.$props.meta(this)
    },
    vBind () {
      return this.opts.vBind
    },
    dataView () {
      return this.opts.dataView
    },
    flatFields () {
      return this.flattenFields(this.opts.fields)
    },
    fields () {
      return this.flatFields.filter(x => x.properties && x.properties.key)
    },
    userMail () {
      return this.$s.userID.value
    },
    noTabs () {
      return this.currentTab === undefined
    },
    headers () {
      const { alignedStartActions, entryActions, status } = this.opts
      const columns = this.flatFields
        .filter(x => x.columnHeader)
        .map((x) => x.columnHeader)
        .filter((x) => {
          if (!x.state) { return x }
        })
      if (this.hasExtreaHeader && this.userRole) {
        const header = []
        this.userRole.forEach((role) => {
          this.opts[`${role}Tabs`].forEach((item) => {
            if (item.tab === this.currentTab && item.extraHeader) {
              header.push(item.extraHeader)
            }
          })
        })
        const extraHeader = [...header.reduce((map, obj) => map.set(obj.text, obj), new Map()).values()]
        extraHeader.forEach((header) => { columns.splice(-2, 0, header) })
      }
      if (status) {
        columns.push(status[0].columnHeader)
      }
      if (entryActions) {
        const actions = {
          text: 'Action',
          value: 'actions',
          sortable: false,
          align: 'center'
        }
        if (alignedStartActions) {
          columns.unshift(actions)
        } else {
          columns.push(actions)
        }
      }
      return columns
    },
    headerVal () {
      const value = this.headers.map(item => item.text)
      value.pop()
      return value
    },
    startAt () {
      return (this.options.page - 1) * this.options.itemsPerPage
    },
    filters () {
      const filter = this.query || {}
      if (this.searchKey && this.search) {
        filter[this.searchKey] = { $regex: this.search }
      }
      return filter
    },
    collectionLabel () {
      return this.opts.label
    },
    tableHeader () {
      return this.opts.tableHeader
    },
    lookupState () {
      return this.$store.state.lookups[this.collectionName]
    },
    hideSelect () {
      return this.opts.hideSelect
    },
    disableSelect () {
      return this.opts.disableItemSelect
    },
    disableItemFunction () {
      return this.opts.disableItemFunction
    },
    selectEnabledItems () {
      return this.selectedLines.length !== this.list.length - this.disabledCount
    },
    multipleUpdate () {
      return this.opts.multipleUpdate
    },
    updateObject () {
      return this.opts.updateObject
    },
    hideMenuDelete () {
      return this.opts.hideMenuDelete
    },
    parentId () {
      return this.$route.params.id ? `${this.$route.params.id}/` : ''
    },
    tableActions () {
      return this.opts.tableActions
    },
    appendSearchIcon () {
      return this.opts.appendSearchIcon
    },
    customDesignProps () {
      return this.opts.customDesign
    },
    isCheckItemsSelected () {
      return this.selectedLines.length > 0
    },
    userRole () {
      return (this.$s && this.$s.userRole)
    },
    userRoleIs () {
      return role => this.userRole && this.userRole.includes(role)
    },
    userId () {
      return this.$s.user
    },
    onlyAdmin () {
      return this.isAdmin && this.userRole.length === 1
    },
    isAdmin () {
      return (this.userRole && this.userRole.includes('admin'))
    },
    isPic () {
      return (this.userRole && this.userRole.includes('pic'))
    },
    isAssignor () {
      return (this.userRole && this.userRole.includes('assignor'))
    },
    isAsir () {
      return this.collectionName === 'SecurityIncident'
    },
    canAssign () {
      return this.isAssignor && this.currentTab === 'Pending for Assignment'
    },
    canDeactivatePDBNF () {
      return ({ item, action }) => action.id === 'delete-pdbnf' && item._status === 'active' && !this.isAsir
    },
    inAssignTab () {
      return this.currentTab === 'Pending for Assignment'
    },
    hasExtreaHeader () {
      return !this.userRoleIs('pic') && !this.userRoleIs('dascoUser') && !this.userRoleIs('divisionChief') && this.collectionName === 'DataBreach'
    },
    headerKey () {
      const header = this.headers.filter(item => item.text === this.filter.header)
      return header[0].value
    },
    fbnerQuery () {
      const statuses = this.opts['for-fbner-query'].statusQuery.$in
      for (const role of this.userRole) {
        if (this.opts.fbnerRoles.includes(role)) {
          const status = this.opts.statuses[`${role}`]
          role === 'reviewer' ? this.opts.statuses.reviewer.forEach(status => statuses.push(status)) : statuses.push(status)
        }
      }
      const fbnerQuery = {
        statusQuery: {
          $in: [
            ...statuses, 'foradjudication', 'forinvestigation', 'forenforcement'
          ]
        },
        $or: [
          { 'assignedTo.evaluator._id': this.userId },
          { 'assignedTo.final._id': this.userId },
          { 'assignedTo.firstPass._id': this.userId },
          { 'recipient.investigation._id': this.userId },
          { 'recipient.adjudication._id': this.userId },
          { 'recipient.enforcement._id': this.userId }
        ]
      }
      return fbnerQuery
    },
    tabs () {
      if (!this.hasExtreaHeader || !this.userRole) return []
      const tabs = []
      this.userRole.forEach(role => tabs.push(...this.opts[`${role}Tabs`]))
      return [...new Set(tabs.map(x => x.tab))].sort()
    },
    currentTab () {
      return this.tabs[this.selectedTab]
    }
  },
  watch: {
    collectionName () {
      this.searchWord = ''
      this.populateTable()
    },
    list (val) {
      this.disableSelect && (this.disabledCount = val.filter((i) => !i.isSelectable).length)
    },
    async searchWord () {
      if (!this.searchWord.length) {
        this.enteredSearchWord = false
        await this.populateTable()
        this.options.page = 1
        this.$refs.search.focus()
      }
    },
    currentTab (val) {
      this.resetFilter()
      this.filter.init = false
      this.assignTab = this.inAssignTab
      this.searchWord = ''
      if (val && this.init) this.populateTable()
    },
    'filter.header': function () {
      if (!this.filter.header) return
      this.filter.boxVal = []
      this.filterItems('items')
    },
    'filter.boxVal': function () {
      this.options.page = 1
      if (this.filter.boxVal.length > 0) this.filterItems('filter')
      if (!this.filter.header) return
      if (this.filter.boxVal.length === 0) this.populateTable()
    }
  },
  created () {
    if (this.$route.name === 'DataBreachTableAssign') {
      const tab = this.tabs.findIndex(x => x === 'Pending for Assignment')
      this.selectedTab = tab
    }
  },
  methods: {
    close () {
      this.showDeadlineDialog = false
    },
    toDateAndTime (date) {
      return dateAndTimeCreated(date)
    },
    removeTime (date) {
      return date && date.split(' ')[0]
    },
    listFilter (list) {
      const filtered = this.opts.listFilter(list)
      return filtered
    },
    async rowClick (item, row) {
      await row.select(true)
      this.$emit('itemSelect', item)
    },
    api (action) {
      return crud(this.opts.route[`${action}`] || this.collectionName)
    },
    flattenFields (fields) {
      return fields.flatMap((x) => {
        let v = [x]
        if (x.children) {
          v = v.concat(this.flattenFields(x.children))
        }
        return v
      })
    },
    capitalize (value) {
      if (value) return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
      return ''
    },
    async filterItems (action) {
      if (this.allItems.length === 0) return
      let filtered = await this.opts.headerKeys[`${this.headerKey}`][`${action}`]
      if (!filtered) {
        const firstPass = await this.allItems.map(item => item.assignedTo.firstPass.fullName)
        const final = await this.allItems.map(item => item.assignedTo.final.fullName)
        filtered = firstPass.concat(final)
      }
      const list = [...new Set(filtered)]
      if (action === 'items') this.filter.checkbox = list.filter(Boolean)
      else {
        this.total = list.length
        this.list = list.length >= 10 ? list.splice(this.startAt, this.options.itemsPerPage) : list
      }
    },
    resetFilter () {
      this.options.page = 1
      this.filter.header = ''
      this.filter.checkbox = []
      this.filter.boxVal = []
    },
    async openDeadline () {
      this.showDeadlineDialog = true
      this.editDeadline = false
      const resp = await deadline.getDeadline()
      const data = await resp.json()
      const currYear = new Date().getFullYear()
      const deadlineYear = data.entries.find((i) => {
        return parseInt(i.year) === currYear
      })
      this.deadlineId = deadlineYear._id
      this.startMonth = deadlineYear.startMonth
      this.defaultStartMonth = deadlineYear.startMonth
      this.endMonth = deadlineYear.endMonth
      this.defaultEndMonth = deadlineYear.endMonth
    },
    async setDeadline () {
      this.$v.$touch()
      this.evaluator = 'temp_val'
      this.reviewer = 'temp_val'
      this.finalReviewer = 'temp_val'
      this.$set(this.formItem, 'startMonth', this.startMonth)
      this.$set(this.formItem, 'endMonth', this.endMonth)
      if (this.$v.$invalid) {
        return this.$store.dispatch('showAlert', {
          message: 'Cannot set ASIR Deadline.',
          type: 'error'
        })
      }
      this.loading = true
      const schema = { startMonth: this.startMonth, endMonth: this.endMonth }
      const resp = await this.collection.update(this.deadlineId, schema)
      if (resp.ok) {
        this.$store.dispatch('showAlert', {
          message: 'ASIR Deadline has been changed successfully!',
          type: 'success'
        })
      } else {
        this.$store.dispatch('showAlert', {
          message: (await resp.json()).error,
          type: 'error'
        })
      }
      this.evaluator = ''
      this.reviewer = ''
      this.finalReviewer = ''
      this.loading = false
      this.openDeadline()
    },
    async getAllItems () {
      if (this.filter.init) return
      this.filter.loading = true
      const allData = await this.api('list').list({ advancedQuery: this.query, dataview: this.dataView, count: this.total })
      this.allItems = allData.entries
      this.filter.loading = false
      this.filter.init = true
    },
    async setQuery () {
      let tabQuery
      if (!this.noTabs) {
        const queryKey = this.currentTab.replaceAll(' ', '-').toLowerCase()
        tabQuery = this.currentTab === 'For Fbner' ? this.fbnerQuery : this.opts[`${queryKey}-query`]
      } else {
        if (!this.isAsir) {
          tabQuery = this.isPic ? { 'pic._id': { $eq: this.userId } } : this.opts['all-query']
        } else {
          tabQuery = this.isPic ? { createdBy: this.$s.user } : this.opts['all-query']
        }
      }
      this.query = tabQuery
    },
    async userProfile () {
      const resp = await this.api('viewMyProfile').read()
      if (resp.ok) { return resp.entry }
    },
    async updateMultiple () {
      const resp = await this.api('updateMultiple').updateMultiple({ info: this.updateObject })
      const data = await resp.json()
      if (resp.ok) {
        this.$store.dispatch('showAlert', {
          message: `${this.collectionLabel} updated successfully`,
          type: 'success'
        })
      } else {
        this.$store.dispatch('showAlert', {
          message: data.error || data.message,
          type: 'error'
        })
      }
      this.updateStatus = false
    },
    selectAllToggle (value) {
      this.selectedLines = []
      if (this.selectEnabledItems) {
        value.items.forEach(item => item.isSelectable && this.selectedLines.push(item))
      }
    },
    async populateTable (action) {
      if (this.filter.boxVal.length > 0) return this.filterItems('filter')
      this.loading = true
      this.selectedLines = []
      await this.setQuery()
      const { itemsPerPage, sortBy, sortDesc } = this.options

      let extQuery = ''
      if (sortBy && sortBy[0]) {
        extQuery = `sortBy=${sortBy[0]}${sortDesc[0] ? '&asc=-1' : ''}`
      } else {
        if (this.collectionName !== 'SecurityIncident') {
          if (this.currentTab === 'For Evaluation' || this.currentTab === 'For Review') {
            extQuery = ''
          } else {
            extQuery = this.inAssignTab ? '' : `sortBy=originalDateOfNotification${sortDesc[0] ? '' : '&asc=-1'}`
          }
        }
      }

      const payload = {
        filter: this.filters,
        start: this.startAt,
        count: itemsPerPage,
        search: action === 'search' ? this.searchWord : '',
        dataview: this.inAssignTab ? 'assignment' : this.dataView,
        advancedQuery: this.query,
        extQuery
      }
      const data = await this.api('list').list(payload)
      this.list = data.entries
      this.total = data.total
      this.loading = false
      if (!this.init) this.init = true
      if (this.inAssignTab && this.options.page === 1 && !this.priority) this.priority = data.entries[0]._id
    },
    async deleteItems (callback) {
      this.loading = true
      const ids = this.selectedLines.map(selected => selected._id)
      const resp = await this.api('deleteMultiple').deleteMultiple(ids)
      const { error, entry, errors, successes } = await resp.json()
      if (resp.ok) {
        this.$store.dispatch('showAlert', {
          message: `${pluralize(this, entry.deletedCount, this.collectionLabel)} deleted`,
          type: 'success'
        })
      } else {
        if (errors || successes) {
          await this.$store.dispatch('showDialog', {
            removeCancel: true,
            title: `Delete ${this.collectionLabel}`,
            buttonLabel: 'OK',
            buttonColor: 'black',
            lists: {
              operation: 'delete',
              successes,
              errors
            }
          })
          await callback()
        } else {
          this.$store.dispatch('showAlert', {
            message: `There was an error encountered. ${error}`,
            type: 'error'
          })
        }
      }
      this.loading = false
      this.populateTable()
    },
    confirm (item) {
      const model = this.opts.route.list.toUpperCase()
      this.$store.dispatch('showDialog', {
        title: `Delete ${model}`,
        message: `Are you sure you want to delete the selected ${model}? This action cannot be undone.`,
        buttonLabel: 'Delete',
        buttonColor: 'error',
        callback: () => this.deleteItem(item._id, model)
      })
    },
    async deleteItem (id, model) {
      this.loading = true
      const resp = await crud(model.toLowerCase()).delete(id)
      const message = `${model} has been successfully deleted`
      this.$store.dispatch('showAlert', {
        message: resp.ok ? message : (await resp.json()).error,
        type: resp.ok ? 'delete' : 'error'
      })
      await this.populateTable()
      this.loading = false
    },
    upload () {
      return this.api('upload').upload({ file: this.file })
    },
    deactivatePDBNF ({ dbnNumber, _id }) {
      if (!this.isAdmin) return
      this.$store.dispatch('showDialog', {
        title: 'Invalidate PDBN',
        message: `Are you sure you want to invalidate ${dbnNumber}? This action cannot be undone.`,
        titleColor: 'red',
        buttonLabel: 'Invalidate',
        buttonColor: 'red',
        callback: async () => {
          const resp = await crud(`pdbnf/${_id}/invalidate`).update()
          const message = `You have successfully invalidated ${dbnNumber}.`
          this.$store.dispatch('showAlert', {
            message: (await resp.json()).error || message,
            type: resp.ok ? 'invalid' : 'error'
          })
          this.populateTable()
        }
      })
    },
    deleteDialog (callback = () => {}) {
      if (this.selectedLines.length === 0) {
        this.$store.dispatch('showDialog', {
          removeCancel: true,
          title: `Select ${this.collectionLabel} to be deleted`,
          buttonLabel: 'OK',
          buttonColor: 'black'
        })
      } else {
        const label = pluralize(this, this.selectedLines.length, this.collectionLabel)
        this.$store.dispatch('showDialog', {
          title: `Delete ${label}`,
          message: `Are you sure you want to delete the selected ${label}? This action cannot be undone.`,
          buttonLabel: 'Delete',
          callback: () => { this.deleteItems(callback) }
        })
      }
    },
    customDesign (option) {
      return this.opts.customDesign && this.opts.customDesign.name === option
    }
  }
}
</script>

<style scoped>
.v-tab--active{
  background-color: rgb(225, 225, 225)
}
.v-tab {
  border-bottom: thin solid rgba(0, 0, 0, 0.12)
}
.calendarIcon {
  position: absolute;
  top:15%;
  right: 5%;
}

.transparent {
  opacity: 0.21;
 }

.textOpaque {
  opacity: 1;
 }
</style>
